import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { entityDetailKey } from "@/lib/queryKeys";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceAssistantTextarea } from "@/components/ui/VoiceAssistantTextarea";
import { Checkbox } from "@/components/ui/checkbox";
import type { AutocompleteOption } from "@/components/ui/autocomplete";
import { useDeals } from "@/hooks/useDeals";
import { CalendarIcon, CheckCircle, User, Building2, Target, Link2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { showInvalidFormFeedback } from "@/lib/invalidFormFeedback";
import { Spinner } from "@/components/ui/spinner";
import { CrmRecordAutocomplete } from "@/components/ui/crm-record-autocomplete";
import { MartinaOutputRating } from "@/components/exai/MartinaOutputRating";
import { useAuth } from "@/hooks/useAuth";
import {
  DEAL_SYNERGY_CLOSEOUT_ACTIONS,
  dealSynergyCloseoutActionLabel,
  isDealSynergyCloseoutTaskKey,
  type DealSynergyCloseoutAction,
} from "@shared/dealSynergyCloseout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { operationalUiErrorMessage } from "@/lib/operationalUiError";

// Zod schema per la validazione del task
const taskSchema = z.object({
  title: z.string().min(3, "Il titolo deve contenere almeno 3 caratteri"),
  description: z.string().optional().nullable(),
  startDateTime: z.date().optional().nullable(),
  endDateTime: z.date().optional().nullable(),
  dueDate: z.date().optional().nullable(),
  taskValue: z.coerce.number().min(0).optional(),
  completed: z.boolean().default(false),
  assignedToId: z.coerce.number().int().positive().optional().nullable(),
  contactId: z.coerce.number().int().positive().optional().nullable(),
  companyId: z.coerce.number().int().positive().optional().nullable(),
  branchId: z.coerce.number().int().positive().optional().nullable(),
  dealId: z.coerce.number().int().positive().optional().nullable(),
  leadId: z.coerce.number().int().positive().optional().nullable(),
  synergyId: z.coerce.number().int().positive().optional().nullable(),
  isCalendarEvent: z.boolean().default(false),
});

type TaskFormValues = z.infer<typeof taskSchema>;

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: any;
  contactId?: number;
  companyId?: number;
  branchId?: number;
  leadId?: number;
  dealId?: number;
  synergyId?: number;
}

export default function TaskModal({
  isOpen,
  onClose,
  initialData,
  contactId,
  companyId,
  branchId,
  leadId,
  dealId,
  synergyId,
}: TaskModalProps) {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [closeoutAction, setCloseoutAction] = useState<DealSynergyCloseoutAction | null>(null);
  const [closeoutNote, setCloseoutNote] = useState("");
  const { user: currentUser } = useAuth();
  const currentUserId = Number(currentUser?.id);
  const defaultOwnerId = Number.isSafeInteger(currentUserId) && currentUserId > 0 ? currentUserId : null;

  // Form con validazione
  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: "",
      description: "",
      startDateTime: null,
      endDateTime: null,
      dueDate: null,
      taskValue: 0,
      completed: false,
      assignedToId: defaultOwnerId,
      contactId: contactId || null,
      companyId: companyId || null,
      branchId: branchId || null,
      leadId: leadId || null,
      dealId: dealId || null,
      synergyId: synergyId || null,
      isCalendarEvent: false,
    },
  });
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(isOpen, form.formState.isDirty || Boolean(closeoutNote.trim()), () => onClose());

  // Fetch dei contatti per il select
  const { data: contacts = [] } = useQuery<any[]>({
    queryKey: ["/api/contacts"],
    enabled: isOpen,
    select: (data: any) => Array.isArray(data) ? data : (data?.data || []),
  });

  // Fetch contatti collegati all'azienda selezionata per priorità
  const watchedContactId = form.watch("contactId");
  const watchedCompanyId = form.watch("companyId");
  const watchedDealId = form.watch("dealId");

  const { data: selectedContactDetail } = useQuery<any>({
    queryKey: [`/api/contacts/${watchedContactId}`],
    queryFn: () => apiRequest(`/api/contacts/${watchedContactId}`),
    enabled: isOpen && !!watchedContactId,
    retry: false,
    staleTime: 60_000,
  });

  const { data: selectedDealDetail } = useQuery<any>({
    queryKey: entityDetailKey('deals', watchedDealId ?? 'pending'),
    queryFn: () => {
      if (watchedDealId == null) throw new Error('DEAL_ID_REQUIRED');
      return apiRequest(`/api/deals/${watchedDealId}`);
    },
    enabled: isOpen && !!watchedDealId,
    retry: false,
    staleTime: 60_000,
  });

  const { data: linkedContacts = [] } = useQuery<any[]>({
    queryKey: [`/api/companies/${watchedCompanyId}/contacts`],
    queryFn: async () => {
      if (!watchedCompanyId) return [];
      const data = await apiRequest(`/api/companies/${watchedCompanyId}/contacts`);
      return data ?? [];
    },
    enabled: isOpen && !!watchedCompanyId,
  });

  // Fetch delle aziende per il select
  const { data: companies = [] } = useQuery<any[]>({
    queryKey: ["/api/companies"],
    enabled: isOpen,
    select: (data: any) => Array.isArray(data) ? data : (data?.data || []),
  });

  // Fetch dei lead per il select
  const { data: leads = [] } = useQuery<any[]>({
    queryKey: ["/api/leads"],
    enabled: isOpen,
    select: (data: any) => Array.isArray(data) ? data : (data?.data || []),
  });

  const contextualSynergyId = Number(initialData?.synergyId ?? synergyId) || null;
  const isDealSynergyCloseoutTask = isDealSynergyCloseoutTaskKey(initialData?.exaiActionKey);
  const isPendingDealSynergyCloseout = isDealSynergyCloseoutTask && !initialData?.completed;
  const { data: contextualSynergy } = useQuery<any>({
    queryKey: [`/api/synergies/${contextualSynergyId}`],
    enabled: isOpen && !!contextualSynergyId,
  });

  // Carica i deal dell'azienda selezionata (o tutti se nessuna azienda è selezionata)
  const { deals: companyDeals = [], isLoading: isLoadingDeals } = useDeals({
    companyId: watchedCompanyId || undefined,
    forCompanyDetail: !!watchedCompanyId,
  });

  // Opzioni per gli autocomplete — contatti con priorità ai linked
  const contactOptions: AutocompleteOption[] = useMemo(() => {
    const linkedIds = new Set((linkedContacts || []).map((c: any) => c.id?.toString()));
    const linkedOptions: AutocompleteOption[] = (linkedContacts || []).map((c: any) => ({
      value: c.id.toString(),
      label: `${c.firstName} ${c.lastName}`,
      icon: <User className="h-4 w-4" />,
      highlighted: true,
    }));
    const otherOptions: AutocompleteOption[] = contacts
      .filter((c: any) => !linkedIds.has(c.id?.toString()))
      .map((c: any) => ({
        value: c.id.toString(),
        label: `${c.firstName} ${c.lastName}`,
        icon: <User className="h-4 w-4" />,
      }));
    const result: AutocompleteOption[] = [];
    if (linkedOptions.length > 0) {
      result.push({ value: '_header_linked', label: '', group: 'Contatti dell\'azienda', groupHeader: true });
      result.push(...linkedOptions);
      if (otherOptions.length > 0) {
        result.push({ value: '_header_others', label: '', group: 'Altri contatti', groupHeader: true });
      }
    }
    result.push(...otherOptions);
    return result;
  }, [contacts, linkedContacts]);

  const companyOptions: AutocompleteOption[] = companies.map((c) => ({
    value: c.id.toString(),
    label: c.name,
    icon: <Building2 className="h-4 w-4" />,
  }));

  const dealOptions: AutocompleteOption[] = (companyDeals || []).map((d: any) => ({
    value: d.id.toString(),
    label: d.name,
    icon: <Target className="h-4 w-4" />,
  }));

  // Un contatto porta sempre con sé l'azienda primaria; un deal porta almeno
  // la sua azienda e, quando disponibile, il contatto principale. Non
  // sovrascriviamo mai una scelta esplicita dell'utente.
  useEffect(() => {
    if (watchedContactId && !watchedCompanyId) {
      const contact = contacts.find((item: any) => Number(item.id) === Number(watchedContactId));
      const resolvedContact = contact ?? selectedContactDetail?.data ?? selectedContactDetail;
      if (resolvedContact?.companyId) form.setValue("companyId", Number(resolvedContact.companyId), { shouldDirty: true });
    }
    if (watchedDealId) {
      const deal = (companyDeals || []).find((item: any) => Number(item.id) === Number(watchedDealId));
      const resolvedDeal = deal ?? selectedDealDetail?.data ?? selectedDealDetail;
      if (resolvedDeal?.companyId && !form.getValues("companyId")) form.setValue("companyId", Number(resolvedDeal.companyId), { shouldDirty: true });
      if (resolvedDeal?.contactId && !form.getValues("contactId")) form.setValue("contactId", Number(resolvedDeal.contactId), { shouldDirty: true });
    }
  }, [watchedContactId, watchedCompanyId, watchedDealId, contacts, companyDeals, selectedContactDetail, selectedDealDetail, form]);

  // Se cambia il deal selezionato, aggiorna il taskValue con il valore del deal
  useEffect(() => {
    const currentDealId = form.getValues("dealId");
    if (currentDealId && companyDeals.length > 0) {
      const selectedDeal = companyDeals.find((d: any) => d.id === currentDealId);
      if (selectedDeal?.dealValue && selectedDeal.dealValue > 0) {
        form.setValue("taskValue", selectedDeal.dealValue);
      }
    }
  }, [form.watch("dealId"), companyDeals]);
  // La lista contestuale e' paginata e non prova l'assenza del Deal nel CRM.
  // Non cancellare una scelta esplicita solo perche' non e' nella prima pagina.

  // Se ci sono dati iniziali, popola il form.
  // Bug 20 lug 2026 (segnalazione Michele): i genitori passano initialData
  // come oggetto inline ricreato a ogni loro render; l'effetto resettava il
  // form a OGNI nuova identita' dell'oggetto, cancellando contenuto e
  // selezioni gia' fatte (titolo, descrizione, deal...). Guardia: il reset
  // avviene solo quando il CONTENUTO applicato cambia davvero.
  const lastAppliedInitKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      lastAppliedInitKeyRef.current = null;
      setCloseoutAction(null);
      setCloseoutNote("");
      return;
    }
    if (initialData) {
      // isUpdating solo se c'è un ID reale (edit), non per follow-up pre-popolati
      const formData = {
        ...initialData,
        startDateTime: initialData.startDateTime ? new Date(initialData.startDateTime) : null,
        endDateTime: initialData.endDateTime ? new Date(initialData.endDateTime) : null,
        dueDate: initialData.dueDate ? new Date(initialData.dueDate) : null,
        taskValue: initialData.taskValue || 0,
        // Merge dei prop entity ID — priorità a initialData, fallback ai prop
        contactId: initialData.contactId ?? contactId ?? null,
        companyId: initialData.companyId ?? companyId ?? null,
        branchId: initialData.branchId ?? branchId ?? null,
        leadId: initialData.leadId ?? leadId ?? null,
        dealId: initialData.dealId ?? dealId ?? null,
        synergyId: initialData.synergyId ?? synergyId ?? null,
        assignedToId: initialData.assignedToId ?? defaultOwnerId,
      };

      const initKey = JSON.stringify(formData);
      if (lastAppliedInitKeyRef.current === initKey) return;
      lastAppliedInitKeyRef.current = initKey;
      setIsUpdating(!!initialData.id);
      form.reset(formData);
    } else {
      // Crea data di inizio (oggi) e data di fine (domani) come predefiniti
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);

      const initKey = `blank:${JSON.stringify([contactId, companyId, branchId, leadId, dealId, synergyId, defaultOwnerId])}`;
      if (lastAppliedInitKeyRef.current === initKey) return;
      lastAppliedInitKeyRef.current = initKey;

      // Reset del form con i valori predefiniti e gli ID passati come props
      form.reset({
        title: "",
        description: "",
        startDateTime: today,
        endDateTime: tomorrow,
        dueDate: null, // dueDate non più necessario
        taskValue: 0,
        completed: false,
        assignedToId: defaultOwnerId,
        contactId: contactId || null,
        companyId: companyId || null,
        branchId: branchId || null,
        leadId: leadId || null,
        dealId: dealId || null,
        synergyId: synergyId || null,
        isCalendarEvent: false,
      });
      setIsUpdating(false);
    }
  }, [isOpen, initialData, form, contactId, companyId, branchId, leadId, dealId, synergyId, defaultOwnerId]);

  // Mutation per la creazione di un nuovo task
  const createTaskMutation = useMutation({
    mutationFn: (data: TaskFormValues) => apiRequest("POST", "/api/tasks", data),
    onSuccess: (returnedTask, data) => {
      // 1. Aggiornamento SINCRONO della cache generale per apparizione istantanea
      const existingTasks = queryClient.getQueryData<any[]>(['/api/tasks']) || [];
      queryClient.setQueryData(['/api/tasks'], [returnedTask, ...existingTasks]);

      // 2. Aggiornamento SINCRONO della cache per entità specifica
      const entityType = data.branchId ? 'branch' : data.companyId ? 'company' : data.contactId ? 'contact' : data.leadId ? 'lead' : data.dealId ? 'deal' : null;
      const entityId = data.branchId || data.companyId || data.contactId || data.leadId || data.dealId;
      if (entityType && entityId) {
        const entityTasks = queryClient.getQueryData<any[]>(['/api/tasks', entityType, entityId]) || [];
        queryClient.setQueryData(['/api/tasks', entityType, entityId], [returnedTask, ...entityTasks]);
      }

      // 3. Invalidazione asincrona per sincronizzazione server in background
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      if (contactId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'contact', contactId] });
      if (companyId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'company', companyId] });
      if (branchId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'branch', branchId] });
      if (leadId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'lead', leadId] });
      if (dealId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'deal', dealId] });
      
      toast({
        title: "Task creato con successo",
        description: "Il task è stato aggiunto correttamente",
      });
      closeAfterSave();
    },
    onError: (error) => {
      console.error("Errore durante la creazione del task:", error);
      toast({
        title: "Errore durante la creazione",
        description: operationalUiErrorMessage(error, "L'attività non è stata creata. Riprova tra poco."),
        variant: "destructive",
      });
    },
  });

  // Mutation per l'aggiornamento di un task esistente
  const updateTaskMutation = useMutation({
    mutationFn: (data: TaskFormValues & { id: number }) => 
      apiRequest("PATCH", `/api/tasks/${data.id}`, data),
    onSuccess: (returnedTask, data) => {
      // 1. Aggiornamento SINCRONO della cache generale
      const existingTasks = queryClient.getQueryData<any[]>(['/api/tasks']) || [];
      queryClient.setQueryData(['/api/tasks'], existingTasks.map(t => t.id === returnedTask.id ? returnedTask : t));

      // 2. Aggiornamento SINCRONO della cache per entità specifica
      const entityType = data.branchId ? 'branch' : data.companyId ? 'company' : data.contactId ? 'contact' : data.leadId ? 'lead' : data.dealId ? 'deal' : null;
      const entityId = data.branchId || data.companyId || data.contactId || data.leadId || data.dealId;
      if (entityType && entityId) {
        const entityTasks = queryClient.getQueryData<any[]>(['/api/tasks', entityType, entityId]) || [];
        queryClient.setQueryData(['/api/tasks', entityType, entityId], entityTasks.map(t => t.id === returnedTask.id ? returnedTask : t));
      }

      // 3. Invalidazione asincrona per sincronizzazione server in background
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      if (contactId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'contact', contactId] });
      if (companyId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'company', companyId] });
      if (branchId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'branch', branchId] });
      if (leadId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'lead', leadId] });
      if (dealId) queryClient.invalidateQueries({ queryKey: ['/api/tasks', 'deal', dealId] });
      
      toast({
        title: "Task aggiornato con successo",
        description: "Le modifiche sono state salvate correttamente",
      });
      closeAfterSave();
    },
    onError: (error) => {
      console.error("Errore durante l'aggiornamento del task:", error);
      toast({
        title: "Errore durante l'aggiornamento",
        description: operationalUiErrorMessage(error, "L'attività non è stata aggiornata. Riprova tra poco."),
        variant: "destructive",
      });
    },
  });

  // Gestione submit del form
  const onSubmit = (data: TaskFormValues) => {
    if (isPendingDealSynergyCloseout && data.completed && !closeoutAction) {
      toast({
        title: "Scegli l'azione conclusiva",
        description: "Prima di chiudere la Sinergia, indica cosa è stato deciso.",
        variant: "destructive",
      });
      return;
    }
    if (isPendingDealSynergyCloseout && data.completed && closeoutAction === 'other' && !closeoutNote.trim()) {
      toast({
        title: "Aggiungi una nota",
        description: "Per “Altra azione” descrivi brevemente l'azione svolta.",
        variant: "destructive",
      });
      return;
    }
    // Per i task nuovi, verifica che entrambe le date siano impostate
    // Per i task esistenti, le date sono opzionali (coerente con creazione senza scadenza)
    if (!isUpdating && (!data.startDateTime || !data.endDateTime)) {
      toast({
        title: "Dati mancanti",
        description: "Sia la data di inizio che quella di conclusione sono obbligatorie per nuovi task",
        variant: "destructive",
      });
      return;
    }

    // Prepara i dati per il backend
    // Le date devono essere inviate come stringhe ISO, il backend le convertirà in oggetti Date
    // La delega non e' ancora disponibile: il backend assegna sempre il task
    // all'identita' autenticata. Non inviare un valore UI nullo o obsoleto.
    const { assignedToId: _assignedToId, ...taskData } = data;
    const formattedData: any = {
      ...taskData,
      startDateTime: data.startDateTime ? data.startDateTime.toISOString() : null,
      endDateTime: data.endDateTime ? data.endDateTime.toISOString() : null,
      dueDate: data.endDateTime ? data.endDateTime.toISOString() : null, // Usa endDateTime come dueDate per retrocompatibilità
      ...(isPendingDealSynergyCloseout && data.completed ? { closeoutAction, closeoutNote } : {}),
    };

    if (isUpdating && initialData?.id) {
      updateTaskMutation.mutate({ ...formattedData, id: initialData.id });
    } else {
      createTaskMutation.mutate(formattedData);
    }
  };

  const isLoading = createTaskMutation.isPending || updateTaskMutation.isPending;
  const originContactId = contactId ?? initialData?.contactId;
  const originCompanyId = companyId ?? initialData?.companyId;
  const originDealId = dealId ?? initialData?.dealId;
  const originBranchId = branchId ?? initialData?.branchId;
  const originLeadId = leadId ?? initialData?.leadId;
  const parentMartinaContext = originContactId ? { entityType: "contact", entityId: String(originContactId), route: `/contacts/${originContactId}` } : originCompanyId ? { entityType: "company", entityId: String(originCompanyId), route: `/companies/${originCompanyId}` } : originDealId ? { entityType: "deal", entityId: String(originDealId), route: `/deals/${originDealId}` } : originBranchId ? { entityType: "branch", entityId: String(originBranchId), route: `/branches/${originBranchId}` } : originLeadId ? { entityType: "lead", entityId: String(originLeadId), route: `/leads/${originLeadId}` } : undefined;
  const taskMartinaContext = initialData?.id
    ? { terminalType: "entity" as const, entityType: "task", entityId: String(initialData.id), route: "/tasks", parentEntityType: parentMartinaContext?.entityType, parentEntityId: parentMartinaContext?.entityId }
    : { terminalType: "entity" as const, ...parentMartinaContext };

  return (
    <Dialog open={isOpen} onOpenChange={requestClose}>
      <DialogContent className="max-w-2xl notion-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900 notion-title">
            {isDealSynergyCloseoutTask ? "Chiudi Sinergia" : isUpdating ? "Modifica Attività" : "Nuova Attività"}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600 mt-1">
            {isPendingDealSynergyCloseout
              ? "Registra l'azione conclusiva: la relazione resterà nello storico del Deal e del soggetto."
              : isUpdating ? "Aggiorna i dettagli dell'attività" : "Crea una nuova attività con tutti i dettagli necessari"}
          </DialogDescription>
        </DialogHeader>

        {initialData?.id && (
          <MartinaOutputRating
            outputType="task"
            outputId={initialData.id}
            label="Quanto e' importante questo task?"
            context={{ surface: 'task_modal' }}
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, showInvalidFormFeedback)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">Titolo*</FormLabel>
                  <FormControl>
                    <Input placeholder="Inserisci il titolo del task" className="notion-input" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isPendingDealSynergyCloseout && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
                <div>
                  <p className="text-sm font-medium text-amber-950">Azione conclusiva</p>
                  <p className="mt-0.5 text-xs text-amber-800">Questa scelta sarà conservata con il Task e terminerà la Sinergia senza cancellarne lo storico.</p>
                </div>
                <Select value={closeoutAction ?? undefined} onValueChange={(value) => setCloseoutAction(value as DealSynergyCloseoutAction)}>
                  <SelectTrigger aria-label="Azione conclusiva della Sinergia">
                    <SelectValue placeholder="Scegli cosa è stato deciso" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_SYNERGY_CLOSEOUT_ACTIONS.map((action) => (
                      <SelectItem key={action} value={action}>{dealSynergyCloseoutActionLabel(action)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {closeoutAction === 'other' && (
                  <Input
                    value={closeoutNote}
                    onChange={(event) => setCloseoutNote(event.target.value)}
                    placeholder="Descrivi l'azione svolta"
                    aria-label="Nota sull'altra azione conclusiva"
                  />
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrizione</FormLabel>
                  <FormControl>
                    <VoiceAssistantTextarea
                      placeholder="Descrivi il task..."
                      className="h-20 resize-none"
                      value={field.value || ""}
                      onValueChange={field.onChange}
                      fieldLabel="Descrizione task"
                      martinaContext={taskMartinaContext}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <FormField
                control={form.control}
                name="startDateTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data di inizio</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value ? format(field.value, "yyyy-MM-dd'T'HH:mm") : ""}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          field.onChange(date);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="endDateTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data di conclusione</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        value={field.value ? format(field.value, "yyyy-MM-dd'T'HH:mm") : ""}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          field.onChange(date);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="contactId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contatto</FormLabel>
                  <FormControl>
                    <CrmRecordAutocomplete
                      entityType="contact"
                      initialOptions={contactOptions}
                      companyScopeId={watchedCompanyId || null}
                      value={field.value?.toString() || ""}
                      onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                      placeholder="Cerca contatto..."
                      selectedLabel={contactOptions.find((option) => option.value === field.value?.toString())?.label}
                      icon={<User className="h-4 w-4" />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Azienda</FormLabel>
                  <FormControl>
                    <CrmRecordAutocomplete
                      entityType="company"
                      initialOptions={companyOptions}
                      value={field.value?.toString() || ""}
                      onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                      placeholder="Cerca azienda..."
                      selectedLabel={companyOptions.find((option) => option.value === field.value?.toString())?.label}
                      icon={<Building2 className="h-4 w-4" />}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {contextualSynergyId && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1 text-sm font-medium">
                  <Link2 className="h-4 w-4" />
                  Contesto Sinergia
                  <span className="text-xs font-normal text-muted-foreground">(ereditato, non modificabile)</span>
                </div>
                <div className="min-h-10 border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
                  {[contextualSynergy?.dealName, contextualSynergy?.entityName].filter(Boolean).join(' — ') || `Sinergia #${contextualSynergyId}`}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="dealId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal</FormLabel>
                    <FormControl>
                      <CrmRecordAutocomplete
                        entityType="deal"
                        initialOptions={dealOptions}
                        value={field.value?.toString() || ""}
                        onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                        placeholder="Cerca opportunità nel CRM..."
                        selectedLabel={dealOptions.find((option) => option.value === field.value?.toString())?.label}
                        icon={<Target className="h-4 w-4" />}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="assignedToId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assegnato a</FormLabel>
                    <FormControl>
                      <Input
                        value={currentUser?.fullName || currentUser?.username || "Utente corrente"}
                        disabled
                        aria-label="Attività assegnata all'utente corrente"
                        onBlur={field.onBlur}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">L'attività viene assegnata automaticamente a te.</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taskValue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valore (€)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        placeholder="0"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        value={field.value || 0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex items-center justify-between">
              <FormField
                control={form.control}
                name="completed"
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isDealSynergyCloseoutTask && Boolean(initialData?.completed)}
                      id="task-completed-checkbox"
                    />
                    <label htmlFor="task-completed-checkbox" className="text-sm">
                      {isPendingDealSynergyCloseout ? "Conferma l'azione e chiudi la Sinergia" : "Contrassegna come completato"}
                    </label>
                  </div>
                )}
              />

              <FormField
                control={form.control}
                name="isCalendarEvent"
                render={({ field }) => (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      id="calendar-event-checkbox"
                    />
                    <label htmlFor="calendar-event-checkbox" className="text-sm">
                      Aggiungi al calendario
                    </label>
                  </div>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => requestClose(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Spinner className="mr-2 h-4 w-4" />}
                {isUpdating ? "Aggiorna" : "Crea"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
